/**
 * Playback of synthesized assistant replies.
 *
 * Audio comes from our own `/api/voice/speech` proxy rather than ElevenLabs
 * directly, because there's no client-side token for synthesis — proxying is
 * what keeps the API key server-side (see `api/voice.py`).
 *
 * ## Two iOS constraints shape this whole file
 *
 * 1. **Audio must be unlocked by a user gesture.** A page that has never played
 *    audio during a tap can't play it later from a network callback. So the
 *    element is created and nudged inside the "Start voice" tap
 *    ({@link Speaker.unlock}), long before there's anything to say.
 * 2. **One element, reused forever.** Creating a fresh `Audio` per reply loses
 *    the unlock, and on iOS the second one simply never plays.
 *
 * ## Why the reply isn't streamed into playback
 *
 * The whole clip is fetched, then played. Progressive playback would need
 * MediaSource — which iOS Safari only exposes as `ManagedMediaSource`, and only
 * recently — for a reply the voice-mode prompt deliberately keeps to one or two
 * sentences. At that length, Flash v2.5 synthesizes in a few hundred
 * milliseconds and the extra machinery would buy less than it costs. If replies
 * ever get longer, this is the place to revisit.
 */

export class SpeechError extends Error {}

/** Fetches synthesized audio for one reply. Injected so the auth token stays in `useApi`. */
export type SpeechFetcher = (text: string, signal: AbortSignal) => Promise<Blob>;

/**
 * A 100ms silent WAV. Playing this during the start tap is what satisfies
 * iOS's gesture requirement — real audio arrives too late to count.
 */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

export class Speaker {
  private readonly audio: HTMLAudioElement;
  /** Aborts the in-flight synthesis request when playback is cut short. */
  private inflight: AbortController | null = null;
  private objectUrl: string | null = null;
  /** Resolves the current {@link speak}; held so barge-in can settle it early. */
  private finish: (() => void) | null = null;

  constructor(private readonly fetchSpeech: SpeechFetcher) {
    this.audio = new Audio();
    this.audio.preload = "auto";
    // Route to the loudspeaker/car rather than the earpiece on mobile.
    this.audio.setAttribute("playsinline", "true");
  }

  /**
   * Prime audio output. Call synchronously inside the user's start tap.
   *
   * Failure is not fatal — some browsers need no unlocking at all — so this
   * swallows errors rather than blocking the session on a silent clip.
   */
  async unlock(): Promise<void> {
    try {
      this.audio.src = SILENT_WAV;
      await this.audio.play();
      this.audio.pause();
      this.audio.currentTime = 0;
    } catch {
      // Playing real audio later may still work; let it try.
    }
  }

  /** True while a reply is being fetched or played — both are "the assistant has the floor". */
  get speaking(): boolean {
    return this.inflight !== null || this.finish !== null;
  }

  /**
   * Speak `text`, resolving when playback finishes — or when {@link stop}
   * interrupts it.
   *
   * Resolving (rather than rejecting) on interruption is deliberate: barge-in
   * is a normal, successful outcome of a conversation, and the caller's next
   * move — reopen the mic — is the same either way.
   */
  async speak(text: string): Promise<void> {
    this.stop();

    const controller = new AbortController();
    this.inflight = controller;

    // Only ever clear the slot if it still holds OUR controller. `speak()` can
    // be re-entered (barge-in, then a new turn) while this fetch is in flight;
    // an unconditional `this.inflight = null` from the older call would drop
    // the newer request's controller on the floor, leaving a stop() with
    // nothing to abort and a reply that plays over the user.
    const releaseSlot = () => {
      if (this.inflight === controller) this.inflight = null;
    };

    let blob: Blob;
    try {
      blob = await this.fetchSpeech(text, controller.signal);
    } catch (err) {
      releaseSlot();
      // An abort means the caller already moved on — not a failure to report.
      if (controller.signal.aborted) return;
      throw new SpeechError(
        err instanceof Error && err.message ? err.message : "Couldn't play the reply.",
      );
    }
    releaseSlot();
    // Superseded while the audio was downloading — the newer call owns
    // playback now, so returning here must not touch the element.
    if (controller.signal.aborted || this.inflight !== null) return;

    this.revokeUrl();
    this.objectUrl = URL.createObjectURL(blob);
    this.audio.src = this.objectUrl;

    await new Promise<void>((resolve) => {
      // Idempotent: `ended` and a barge-in `stop()` can both reach for it, and
      // whichever is second must not resolve an already-settled promise.
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        this.audio.onended = null;
        this.audio.onerror = null;
        this.finish = null;
        resolve();
      };
      this.finish = done;
      this.audio.onended = done;
      // A decode or network error ends the turn rather than stranding the
      // session in "speaking" forever with a mic that never reopens.
      this.audio.onerror = done;
      void this.audio.play().catch(done);
    });
  }

  /**
   * Interrupt playback immediately — the barge-in path.
   *
   * Cancels an in-flight synthesis request too: if the user is already talking
   * over a reply that hasn't arrived, playing it when it lands would talk over
   * *them*.
   */
  stop(): void {
    this.inflight?.abort();
    this.inflight = null;
    if (!this.audio.paused) this.audio.pause();
    this.audio.currentTime = 0;
    this.finish?.();
    this.revokeUrl();
  }

  /** Release the element and any outstanding blob. */
  dispose(): void {
    this.stop();
    this.audio.src = "";
  }

  private revokeUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
